import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useMyProfile } from '@/hooks/use-my-profile';
import { api, type OwnProfile } from '@/lib/api';
import { buildProfilePayload, type ProfileDraft } from '@/lib/profile-draft';
import { queryKeys } from '@/lib/query-keys';

// The one mutation wrapper for PATCH /me/profile. Screens pass only the
// fields they actually change; the merge from the account's current
// server state (and the path->id photo normalization the full-replace
// endpoint demands) lives in buildProfilePayload, so no screen can
// forget it. Reads current via useMyProfile -- same query key, retry
// policy, and 404-as-create-path semantics as every other reader.
//
// The write-through on success is universal (the sibling profile
// screens read this same ['me','profile'] entry, so its baseline must
// never be one round trip stale); anything else a screen does after a
// save -- the Profile form's threads/me invalidations and justSaved
// toast, prompts' router.back() -- stays in that screen's onSuccess,
// where its own comments justify it.
export function useProfileSave(options?: { onSuccess?: (profile: OwnProfile) => void }) {
  const queryClient = useQueryClient();
  const ownProfile = useMyProfile();

  return useMutation({
    // ownProfile.data is undefined exactly on the create path (the
    // profile 404s until first save) -- buildProfilePayload treats that
    // as "no current, payload is the whole draft".
    mutationFn: (change: ProfileDraft) => api.updateProfile(buildProfilePayload(ownProfile.data, change)),
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKeys.meProfile(), profile);
      options?.onSuccess?.(profile);
    },
  });
}
